import { useBusinessBriefStatus } from '@doneverse/data/hooks'
import { useRouterState } from '@tanstack/react-router'
import { isNavItemVisible, panelNavItems, resolveRailSection } from './founderNavConfig'
import { SectionSidebar } from './SectionSidebar'
import { useAuth } from '@/contexts/AuthContext'
import { useMobileSidebar } from '@/contexts/MobileSidebarContext'
import { useFeatureFlags } from '@/hooks/useFeatureFlags'
import { useOnboardingLock } from '@/hooks/useOnboardingLock'
import { isRestrictedDoer } from '@/lib/doerAccess'
import { isNavTargetLocked, ONBOARDING_LOCK_MESSAGE } from '@/lib/onboardingLock'

export function AppSidebar() {
  const { profile } = useAuth()
  const { hasFlag } = useFeatureFlags()
  const { previewSection } = useMobileSidebar()
  const pathname = useRouterState({ select: (s) => s.location.pathname })
  const isFounder = profile?.role === 'founder'
  // Founders, doers and admins navigate via a sectioned rail; other roles
  // (doing_team, intake_team) keep a single flat list since their rail has no
  // sub-sections.
  const usesRailSections = isFounder || profile?.role === 'doer' || profile?.role === 'admin'
  const restrictedDoer = isRestrictedDoer(profile, hasFlag)
  const onboardingLock = useOnboardingLock()
  const { needsAttention: businessBriefNeedsAttention } = useBusinessBriefStatus({ enabled: isFounder })

  const attentionByRoute: Record<string, boolean> = {
    '/business-brief': businessBriefNeedsAttention,
  }

  // Each rail icon owns a section, so the sidebar shows only the active
  // section's items. When the rail-tap preview is active (mobile only), it
  // overrides the path-derived section. Paths no section owns (e.g.
  // /notifications) fall back to the roadmap list — empty for admins, whose
  // sections all live under /admin/*.
  const activeSection = previewSection ?? resolveRailSection(pathname, profile?.role) ?? 'roadmap'

  const visibleItems = panelNavItems
    .filter((item) => isNavItemVisible(item, profile?.role, hasFlag, restrictedDoer))
    .filter((item) => !usesRailSections || item.section === activeSection)
    .map((item) => (item.to && attentionByRoute[item.to] ? { ...item, attention: true } : item))
    // Onboarding lock: shown but not clickable until steps 1–4 are done. Only
    // the active step's own surface stays live, which is what the route guard
    // allows through — same predicate, so the menu can't offer a link the guard
    // would immediately bounce.
    .map((item) =>
      isNavTargetLocked(item.to, onboardingLock)
        ? { ...item, locked: true, lockedReason: ONBOARDING_LOCK_MESSAGE }
        : item,
    )

  return <SectionSidebar navItems={visibleItems} showLogo />
}
